// Per-page title, description and canonical for the public pages that don't
// set their own (the SEO landing pages and property pages call useSeo /
// usePageMeta themselves). Applied by <RouteSeo /> in App.js.
//
// Why this exists (2026-10-05): these 12 pages used to inherit the homepage
// defaults from public/index.html, so every one of them told search engines
// and AI crawlers "my canonical is https://ariv.co.uk/". Crawlers treat a page
// like that as a duplicate of the homepage and drop it, which is why only the
// homepage and the landing pages were being seen.
//
// Keep descriptions factual. AI tools quote them as statements about Ariv.
// Paths must match the <loc> entries in public/sitemap.xml exactly.

const SITE = 'https://ariv.co.uk';

// Matches the site default in public/index.html.
const INDEXABLE = 'index, follow, max-image-preview:large';

const page = (path, title, description) => ({
  title,
  description,
  // Trailing slash, deliberately. The static host serves the prerendered file
  // at /about/ — the no-slash /about falls through to the SPA catch-all and
  // returns the empty homepage shell. Until 2026-10-16 the canonical here
  // pointed at the no-slash form, so each real page told crawlers "the
  // canonical version of me is that URL" — and that URL serves a copy of the
  // homepage. The pages disclaimed themselves into the very duplicate-content
  // bug this file exists to fix. public/sitemap.xml advertises the same
  // trailing-slash URLs, which is what the eight SEO landing pages have always
  // done and why only they were being indexed properly.
  canonical: `${SITE}${path}/`,
  // Say plainly that this page is public. build/app.html — the document the
  // host serves for application routes and for URLs that do not exist — is
  // marked noindex, so a public page that renders from it instead of from its
  // own prerendered file would otherwise inherit that and quietly drop out of
  // every index.
  robots: INDEXABLE,
});

export const PAGE_META = {
  '/become-host': page(
    '/become-host',
    'Become a host on Ariv | Start a UK short let, 0% for 90 days',
    'Own a property or start through rent to rent: launch and run a UK short let on Ariv, all in one app. Ariv takes a 13% host-only commission, and the first 100 founding hosts pay 0% for their first 90 days.'
  ),
  '/provider/signup': page(
    '/provider/signup',
    'Join Ariv as a service provider | Cleaning and home services',
    'Offer cleaning and other home services to UK short-let hosts and guests on Ariv. Sign up, choose the services you provide and get matched as local coverage goes live.'
  ),
  '/how-it-works': page(
    '/how-it-works',
    'How Ariv works | Book a stay or run a short let in one app',
    'How Ariv works for guests and hosts: book UK short stays, and list and run a short let with bookings, cleaning, payouts and insights in one app.'
  ),
  '/trust': page(
    '/trust',
    'Trust and safety | How Ariv protects your account and data',
    'How Ariv looks after your account, payments and personal data, explained in plain English.'
  ),
  '/about': page(
    '/about',
    'About Ariv | One app to start and run a UK short let',
    'Ariv started with hosts in London who found running a short let scattered and complicated, so they built one app to do it all. Ariv is operated by Brick & Capital Ltd.'
  ),
  '/help': page(
    '/help',
    'Help Centre | Ariv',
    'Answers to common questions about bookings, payments, hosting, your account and restaurant partners on Ariv, and how to contact our support team.'
  ),
  '/contact': page(
    '/contact',
    'Contact Ariv',
    'Get in touch with Ariv for general enquiries, host support, enterprise sales or press.'
  ),
  '/careers': page(
    '/careers',
    'Careers at Ariv',
    'Careers at Ariv, the UK app for starting and running a short let.'
  ),
  '/signup': page(
    '/signup',
    'Join Ariv | Sign up as a guest, host or partner',
    'Create your Ariv account as a guest, host, landlord, restaurant, delivery rider, driver or service provider.'
  ),
  '/terms': page(
    '/terms',
    'Terms and Conditions | Ariv',
    'The terms for using Ariv, operated by Brick & Capital Ltd: bookings, fees and commission, payouts, cancellations and your rights.'
  ),
  '/privacy': page(
    '/privacy',
    'Privacy Policy | Ariv',
    'How Brick & Capital Ltd, trading as Ariv, collects, uses and protects your personal data under UK GDPR, and how to exercise your rights.'
  ),
  '/cookies': page(
    '/cookies',
    'Cookie Policy | Ariv',
    'The cookies and similar technologies Ariv uses, why we use them and how to change your choice at any time.'
  ),
  '/host/calculator': page(
    '/host/calculator',
    'Short let profit calculator | What a UK listing really earns | Ariv',
    'Work out what a UK short let earns before you commit. Put in the nightly rate, nights booked, cleaning fee, rent or mortgage and running costs to see monthly and annual profit, return on rent and break-even nights.'
  ),
  '/host/resources': page(
    '/host/resources',
    'Host Resource Centre | Guides and templates for UK short lets | Ariv',
    'Guides and templates for UK short-let hosts: pricing, photography, five-star reviews, rent-to-rent, tax and scaling from one property to ten, plus a cleaning checklist, house rules, inventory list and R2R contract.'
  ),
  // NOT in sitemap.xml and NOT prerendered, deliberately. This page loads
  // providers from the API and renders "No providers found" when there are
  // none, so a build-time snapshot would bake that sentence into the HTML
  // every crawler reads — worse than not being prerendered at all. The entry
  // is here so the page still gets its own title, description and canonical
  // (and `robots: index`) once React runs, which is what Google sees. Add it
  // to the sitemap and to scripts/prerender-routes.js once there are enough
  // live providers that an empty state is no longer the likely snapshot.
  '/services': page(
    '/services',
    'Find service providers | Cleaning for UK short lets | Ariv',
    'Find cleaning and home-service providers for a UK short let. Browse by category and area, or post a request and let local providers come to you with quotes.'
  ),
};

/** "/about/" -> "/about"; "/" stays "/". */
export const normalisePath = (pathname) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : pathname;
